import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Field } from '@/components/synapse/Field';
import { Icon, type IconName } from '@/components/synapse/Icon';
import { TypePill } from '@/components/synapse/TypePill';
import { TypeSegment } from '@/components/synapse/TypeSegment';
import { ScanDialog } from '@/components/synapse/ScanDialog';
import { ComboboxListbox } from '@/components/synapse/ComboboxListbox';
import { SEARCH_MIN_CHARS, useItemsSearch, useStockIn } from '@/hooks/useInventory';
import { useCombobox } from '@/hooks/useCombobox';
import { useMedicineSuggest } from '@/hooks/useMedicineSuggest';
import { useDebounced } from '@/hooks/useDebounced';
import type { Item, MedicineSuggestion, StockInEntry } from '@/types/api';
import {
  ITEM_TYPE_CONFIG,
  ITEM_TYPE_LABEL_KEY,
  UNIT_OPTIONS,
  dosageFormToUnit,
  isFieldRequired,
  isFieldVisible,
  translateUnit,
  type ItemFieldKey,
  type ItemType,
} from '@/lib/itemTypeConfig';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';

// One row in the pending batch list. Fields that are hidden for the chosen
// type are still kept on the row (it makes editing cheap) but are stripped at
// submit time so the backend never sees them.
interface BatchRow {
  rowId: string;
  /** Set when the user picked an existing SKU (auto-suggest or scan match). */
  itemId: string | null;
  type: ItemType;
  brandName: string;
  genericName: string;
  strength: string;
  unit: string;
  /** Optional barcode — typed manually, carried over from an unmatched scan, or
   *  inherited from a matched SKU. */
  barcode: string;
  /** True when the barcode came from a scan (matched or unmatched). While locked
   *  the field is read-only; clearing the form or picking a different brand
   *  unlocks it. */
  barcodeLocked: boolean;
  quantity: number;
  batchNumber: string;
  expiryDate: string;
  manufacturer: string;
  storageCondition: string;
  notes: string;
}

const TYPE_ICON: Record<ItemType, IconName> = {
  medication: 'pill',
  consumable: 'box',
  medical_device: 'device',
  reagent: 'flask',
  other: 'box_in',
};

function emptyDraft(type: ItemType = 'medication'): BatchRow {
  return {
    rowId: '',
    itemId: null,
    type,
    brandName: '',
    genericName: '',
    strength: '',
    unit: defaultUnitFor(type),
    barcode: '',
    barcodeLocked: false,
    quantity: 1,
    batchNumber: '',
    expiryDate: '',
    manufacturer: '',
    storageCondition: '',
    notes: '',
  };
}

function defaultUnitFor(type: ItemType): string {
  return UNIT_OPTIONS[type][0] ?? '';
}

// Clear values for fields that are hidden under the new type. Avoids the form
// silently submitting stale data the user can't see.
function applyTypeChange(d: BatchRow, type: ItemType): BatchRow {
  const cfg = ITEM_TYPE_CONFIG[type];
  return {
    ...d,
    type,
    genericName: cfg.genericName === 'hidden' ? '' : d.genericName,
    strength: cfg.strength === 'hidden' ? '' : d.strength,
    batchNumber: cfg.batchNumber === 'hidden' ? '' : d.batchNumber,
    barcode: cfg.barcode === 'hidden' ? '' : d.barcode,
    expiryDate: cfg.expiryDate === 'hidden' ? '' : d.expiryDate,
    manufacturer: cfg.manufacturer === 'hidden' ? '' : d.manufacturer,
    storageCondition: cfg.storageCondition === 'hidden' ? '' : d.storageCondition,
    notes: cfg.notes === 'hidden' ? '' : d.notes,
    // Default the unit when the field hasn't been touched OR the previous unit
    // isn't suggested for the new type. Preserves a custom typed unit if the
    // user already entered one for this type.
    unit: UNIT_OPTIONS[type].includes(d.unit) ? d.unit : defaultUnitFor(type),
  };
}

function rowToEntry(row: BatchRow): StockInEntry {
  const cfg = ITEM_TYPE_CONFIG[row.type];
  const base: Pick<StockInEntry, 'quantity' | 'batchNumber' | 'expiryDate'> = {
    quantity: row.quantity,
    batchNumber:
      cfg.batchNumber !== 'hidden' && row.batchNumber.trim() ? row.batchNumber.trim() : null,
    expiryDate: cfg.expiryDate !== 'hidden' && row.expiryDate ? row.expiryDate : null,
  };
  if (row.itemId) {
    return { ...base, itemId: row.itemId };
  }
  // Strip hidden fields from the newItem payload — the backend Zod schema
  // rejects them, and we want a strict round-trip.
  const newItem: StockInEntry['newItem'] = {
    type: row.type,
    brandName: row.brandName.trim(),
    unit: row.unit.trim(),
    genericName:
      cfg.genericName !== 'hidden' && row.genericName.trim() ? row.genericName.trim() : null,
    strength: cfg.strength !== 'hidden' && row.strength.trim() ? row.strength.trim() : null,
    barcode: cfg.barcode !== 'hidden' && row.barcode.trim() ? row.barcode.trim() : null,
    manufacturer:
      cfg.manufacturer !== 'hidden' && row.manufacturer.trim() ? row.manufacturer.trim() : null,
    storageCondition:
      cfg.storageCondition !== 'hidden' && row.storageCondition.trim()
        ? row.storageCondition.trim()
        : null,
    notes: cfg.notes !== 'hidden' && row.notes.trim() ? row.notes.trim() : null,
  };
  return { ...base, newItem };
}

function isStringFieldRequiredAndEmpty(
  d: BatchRow,
  field: Exclude<ItemFieldKey, 'quantity' | 'unit'>,
): boolean {
  if (!isFieldRequired(d.type, field)) return false;
  const v = (d[field] as string) ?? '';
  return !v.trim();
}

function isPastDate(yyyymmdd: string): boolean {
  if (!yyyymmdd) return false;
  const d = new Date(`${yyyymmdd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

export function AddStockPage() {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<BatchRow>(() => emptyDraft());
  const [pending, setPending] = useState<BatchRow[]>([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [submitted, setSubmitted] = useState<{ count: number; units: number } | null>(null);
  const stockIn = useStockIn();

  const draftValid = useMemo(() => {
    if (!Number.isInteger(draft.quantity) || draft.quantity <= 0) return false;
    if (!draft.unit.trim()) return false;
    const stringFields: Exclude<ItemFieldKey, 'quantity' | 'unit'>[] = [
      'brandName',
      'genericName',
      'strength',
      'batchNumber',
      'barcode',
      'expiryDate',
      'manufacturer',
      'storageCondition',
      'notes',
    ];
    for (const f of stringFields) {
      if (isStringFieldRequiredAndEmpty(draft, f)) return false;
    }
    return true;
  }, [draft]);

  const totals = useMemo(
    () => ({
      count: pending.length,
      units: pending.reduce((acc, r) => acc + r.quantity, 0),
    }),
    [pending],
  );

  const addOrUpdate = () => {
    if (!draftValid) return;
    setPending((prev) => {
      if (draft.rowId) {
        return prev.map((r) => (r.rowId === draft.rowId ? draft : r));
      }
      return [...prev, { ...draft, rowId: crypto.randomUUID() }];
    });
    setDraft(emptyDraft(draft.type));
  };

  const editRow = (rowId: string) => {
    const row = pending.find((r) => r.rowId === rowId);
    if (!row) return;
    setDraft({ ...row, barcodeLocked: false });
  };

  const removeRow = (rowId: string) => {
    setPending((prev) => prev.filter((r) => r.rowId !== rowId));
  };

  // Fill `current` only if the user hasn't typed anything there yet. Picking a
  // suggestion is a hint — it must never overwrite a value the user typed
  // (the catalogue is sometimes wrong and clinics may legitimately stock SKUs
  // that don't match it).
  const fillIfEmpty = (current: string, suggested: string | null | undefined) =>
    current.trim().length === 0 && suggested ? suggested : current;

  // Same idea as fillIfEmpty for the unit field, but additionally treats the
  // type's default unit as "untouched". `emptyDraft` and `applyTypeChange`
  // populate `unit` automatically from UNIT_OPTIONS[type][0], so a draft with
  // unit === defaultUnitFor(type) hasn't really been chosen by the user — we
  // should still let a suggestion overwrite it.
  const fillUnitIfUntouched = (
    currentUnit: string,
    currentType: ItemType,
    suggested: string | null | undefined,
  ) => {
    if (!suggested) return currentUnit;
    const untouched =
      currentUnit.trim().length === 0 || currentUnit === defaultUnitFor(currentType);
    return untouched ? suggested : currentUnit;
  };

  const onPickSuggestion = (s: MedicineSuggestion) => {
    setDraft((d) => {
      if (s.source === 'item') {
        // Picking an existing SKU LINKS the row. The brand and `type` are
        // canonical (the user implicitly accepted them by clicking the
        // suggestion) and overwrite the draft. Everything else only fills if
        // the user hasn't typed it yet — preserving any typed-ahead values
        // like a specific strength variant.
        return {
          ...d,
          itemId: s.itemId,
          type: s.type,
          brandName: s.brandName,
          genericName: fillIfEmpty(d.genericName, s.genericName),
          strength: fillIfEmpty(d.strength, s.strength),
          unit: fillUnitIfUntouched(d.unit, s.type, s.unit),
          barcode: fillIfEmpty(d.barcode, s.barcode),
          manufacturer: fillIfEmpty(d.manufacturer, s.manufacturer),
          storageCondition: fillIfEmpty(d.storageCondition, s.storageCondition),
          notes: fillIfEmpty(d.notes, s.notes),
          barcodeLocked: false,
        };
      }
      // Reference catalogue pick: hint-only. Brand is overwritten (the user
      // accepted it by clicking), other fields only fill if empty. itemId
      // stays null — the row will be submitted as a new item. Release the
      // scan lock for the same reason as the item branch: the user has
      // explicitly indicated this is a different product than what they
      // scanned, so the barcode field should become editable again.
      return {
        ...d,
        itemId: null,
        brandName: s.brandName ?? d.brandName,
        genericName: fillIfEmpty(d.genericName, s.genericName),
        strength: fillIfEmpty(d.strength, s.strength),
        manufacturer: fillIfEmpty(d.manufacturer, s.manufacturer),
        unit: fillUnitIfUntouched(d.unit, d.type, dosageFormToUnit(s.dosageForm)),
        barcodeLocked: false,
      };
    });
  };

  const onScanResolved = (match: Item | null, barcode: string) => {
    if (match) {
      setDraft((d) => ({
        ...d,
        itemId: match.id,
        type: match.type,
        brandName: match.brandName,
        genericName: match.genericName ?? '',
        strength: match.strength ?? '',
        unit: match.unit,
        barcode: match.barcode ?? barcode,
        manufacturer: match.manufacturer ?? '',
        storageCondition: match.storageCondition ?? '',
        notes: match.notes ?? '',
        barcodeLocked: true,
      }));
    } else {
      setDraft((d) => ({ ...d, itemId: null, barcode, barcodeLocked: true }));
    }
    setScanOpen(false);
  };

  const onSubmit = async () => {
    if (pending.length === 0) return;
    const entries = pending.map(rowToEntry);
    try {
      await stockIn.mutateAsync({ entries });
      setSubmitted({ count: totals.count, units: totals.units });
      setPending([]);
      setDraft(emptyDraft());
    } catch {
      // ApiError captured into stockIn.error for inline rendering below.
    }
  };

  if (submitted) {
    return (
      <SubmitSuccess
        count={submitted.count}
        units={submitted.units}
        onStartAnother={() => setSubmitted(null)}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1180px]">
      <header className="mb-5 flex flex-col gap-1.5 sm:mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">{t('addStock.title')}</h1>
        <p className="text-[13.5px] text-muted">{t('addStock.subtitle')}</p>
      </header>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.05fr] lg:items-start lg:gap-6">
        {/* LEFT — entry */}
        <div className="flex flex-col gap-3">
          <Card className="overflow-hidden p-0">
            <CardContent className="p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
                <button
                  type="button"
                  onClick={() => setScanOpen(true)}
                  className="flex h-[88px] flex-1 items-center gap-4 rounded-lg bg-accent px-5 text-left text-accent-foreground transition-opacity hover:opacity-90"
                >
                  <span className="flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-white/10">
                    <Icon name="scan" size={24} />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-[16px] font-semibold tracking-[-0.02em]">
                      {t('addStock.scanBarcode')}
                    </span>
                    <span className="mt-0.5 text-xs opacity-75">{t('addStock.scanHint')}</span>
                  </span>
                </button>
                <div className="hidden w-[200px] flex-col justify-center gap-1 rounded-lg border border-dashed border-line-2 px-4 py-3.5 sm:flex">
                  <div className="text-[13px] font-semibold">{t('addStock.orEnterManually')}</div>
                  <div className="text-[11.5px] text-muted">{t('addStock.orEnterManuallyHint')}</div>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3.5">
                <Field label={t('addStock.itemType')}>
                  <TypeSegment
                    value={draft.type}
                    onChange={(t) => setDraft((d) => applyTypeChange(d, t))}
                  />
                </Field>

                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  <BrandSuggestField
                    type={draft.type}
                    value={draft.brandName}
                    onChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        brandName: v,
                        // Typing into brand un-links a previously picked SKU
                        // so we don't silently submit a stale itemId.
                        itemId: d.brandName === v ? d.itemId : null,
                      }))
                    }
                    onPick={onPickSuggestion}
                  />
                  {isFieldVisible(draft.type, 'genericName') && (
                    <Field label={t('common.genericName')} optional={!isFieldRequired(draft.type, 'genericName')}>
                      <Input
                        value={draft.genericName}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            genericName: e.target.value,
                            itemId: d.genericName === e.target.value ? d.itemId : null,
                          }))
                        }
                        placeholder={t('addStock.placeholderGeneric')}
                      />
                    </Field>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
                  {isFieldVisible(draft.type, 'strength') && (
                    <Field
                      label={t('common.strength')}
                      optional={!isFieldRequired(draft.type, 'strength')}
                    >
                      <Input
                        mono
                        value={draft.strength}
                        onChange={(e) => setDraft((d) => ({ ...d, strength: e.target.value }))}
                        placeholder={t('addStock.placeholderStrength')}
                      />
                    </Field>
                  )}
                  <Field label={t('common.quantity')}>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        aria-label={t('common.decreaseQuantity')}
                        onClick={() =>
                          setDraft((d) => ({ ...d, quantity: Math.max(1, d.quantity - 1) }))
                        }
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2"
                      >
                        <Icon name="minus" size={16} />
                      </button>
                      <Input
                        mono
                        className="flex-1 text-center"
                        type="number"
                        min={1}
                        value={draft.quantity}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            quantity: Math.max(1, Number(e.target.value) || 0),
                          }))
                        }
                      />
                      <button
                        type="button"
                        aria-label={t('common.increaseQuantity')}
                        onClick={() => setDraft((d) => ({ ...d, quantity: d.quantity + 1 }))}
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2"
                      >
                        <Icon name="plus" size={16} />
                      </button>
                    </div>
                  </Field>
                  <UnitField
                    type={draft.type}
                    value={draft.unit}
                    onChange={(v) => setDraft((d) => ({ ...d, unit: v }))}
                  />
                </div>

                {isFieldVisible(draft.type, 'batchNumber') && (
                  <Field
                    label={t('common.batchNumber')}
                    optional={!isFieldRequired(draft.type, 'batchNumber')}
                  >
                    <Input
                      mono
                      value={draft.batchNumber}
                      onChange={(e) => setDraft((d) => ({ ...d, batchNumber: e.target.value }))}
                      placeholder={t('addStock.placeholderBatchNumber')}
                    />
                  </Field>
                )}

                {isFieldVisible(draft.type, 'barcode') && (
                  <Field
                    label={t('common.barcode')}
                    optional={!isFieldRequired(draft.type, 'barcode')}
                    hint={draft.barcodeLocked ? t('addStock.capturedFromScan') : undefined}
                  >
                    <div className="flex gap-1.5">
                      <Input
                        mono
                        readOnly={draft.barcodeLocked}
                        value={draft.barcode}
                        onChange={(e) =>
                          setDraft((d) =>
                            d.barcodeLocked ? d : { ...d, barcode: e.target.value },
                          )
                        }
                        placeholder={t('addStock.placeholderBarcode')}
                        className={draft.barcodeLocked ? 'bg-paper-2 text-ink-2' : undefined}
                      />
                      {draft.barcodeLocked && (
                        <button
                          type="button"
                          onClick={() => setDraft((d) => ({ ...d, barcodeLocked: false }))}
                          className="flex h-10 shrink-0 items-center gap-1 rounded-md border border-line bg-paper px-2.5 text-[12.5px] text-ink-2 hover:bg-paper-2"
                          aria-label={t('addStock.unlockBarcode')}
                        >
                          <Icon name="x" size={14} /> {t('addStock.unlock')}
                        </button>
                      )}
                    </div>
                  </Field>
                )}

                {isFieldVisible(draft.type, 'expiryDate') && (
                  <Field
                    label={t('common.expiry')}
                    optional={!isFieldRequired(draft.type, 'expiryDate')}
                    hint={isPastDate(draft.expiryDate) ? t('addStock.pastDateHint') : undefined}
                  >
                    <Input
                      mono
                      type="date"
                      value={draft.expiryDate}
                      onChange={(e) => setDraft((d) => ({ ...d, expiryDate: e.target.value }))}
                    />
                  </Field>
                )}

                {(isFieldVisible(draft.type, 'manufacturer') ||
                  isFieldVisible(draft.type, 'storageCondition')) && (
                  <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                    {isFieldVisible(draft.type, 'manufacturer') && (
                      <Field
                        label={t('common.manufacturer')}
                        optional={!isFieldRequired(draft.type, 'manufacturer')}
                      >
                        <Input
                          value={draft.manufacturer}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, manufacturer: e.target.value }))
                          }
                          placeholder={t('addStock.placeholderManufacturer')}
                        />
                      </Field>
                    )}
                    {isFieldVisible(draft.type, 'storageCondition') && (
                      <Field
                        label={t('common.storageCondition')}
                        optional={!isFieldRequired(draft.type, 'storageCondition')}
                      >
                        <Input
                          value={draft.storageCondition}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, storageCondition: e.target.value }))
                          }
                          placeholder={t('addStock.placeholderStorage')}
                        />
                      </Field>
                    )}
                  </div>
                )}

                {isFieldVisible(draft.type, 'notes') && (
                  <Field label={t('common.notes')} optional={!isFieldRequired(draft.type, 'notes')}>
                    <Input
                      value={draft.notes}
                      onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                      placeholder={t('addStock.placeholderNotes')}
                    />
                  </Field>
                )}

                {stockIn.isError && (
                  <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
                    {humaniseStockInError(stockIn.error, t)}
                  </div>
                )}

                <div className="mt-1 flex items-center justify-between gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setDraft(emptyDraft(draft.type))}
                    disabled={!isDraftDirty(draft)}
                  >
                    <Icon name="x" size={16} /> {t('addStock.clearForm')}
                  </Button>
                  <Button type="button" onClick={addOrUpdate} disabled={!draftValid}>
                    <Icon name="plus" size={16} />
                    {draft.rowId ? t('addStock.updateItem') : t('addStock.addToBatch')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — running list */}
        <div className="lg:sticky lg:top-4">
          <Card className="overflow-hidden p-0">
            <div className="flex items-center gap-3 border-b border-line px-4 py-3.5">
              <div className="flex-1">
                <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('addStock.thisBatch')}</div>
                <div className="mt-0.5 text-[12px] text-muted tabular-nums">
                  {totals.count === 0
                    ? t('addStock.noItemsYet')
                    : t('addStock.itemsAndUnits', {
                        count: totals.count,
                        itemLabel: t('common.item', { count: totals.count }),
                        units: totals.units,
                        unitLabel: t('common.unit', { count: totals.units }),
                      })}
                </div>
              </div>
              {pending.length > 0 && (
                <span className="inline-flex h-[22px] items-center rounded-full bg-info-bg px-2 text-[11.5px] font-medium text-info">
                  {t('common.draft')}
                </span>
              )}
            </div>

            <div className="max-h-[540px] overflow-auto px-3.5 py-3.5">
              {pending.length === 0 ? (
                <EmptyList />
              ) : (
                <ul className="flex flex-col gap-2">
                  {pending.map((row) => (
                    <li key={row.rowId}>
                      <BatchListRow row={row} onEdit={editRow} onRemove={removeRow} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3.5">
              <div className="text-[12.5px] text-muted">
                {pending.length === 0
                  ? t('addStock.addAtLeastOne')
                  : t('addStock.submitWillRegister', {
                      count: pending.length,
                      itemLabel: t('common.item', { count: pending.length }),
                    })}
              </div>
              <Button
                type="button"
                onClick={onSubmit}
                disabled={pending.length === 0 || stockIn.isPending}
              >
                <Icon name="check" size={16} />
                {stockIn.isPending ? t('addStock.submitting') : t('addStock.submitBatch')}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {scanOpen && (
        <AddStockScanDialog onClose={() => setScanOpen(false)} onResolved={onScanResolved} />
      )}
    </div>
  );
}

function isDraftDirty(d: BatchRow): boolean {
  return Boolean(
    d.brandName ||
      d.genericName ||
      d.strength ||
      d.batchNumber ||
      d.expiryDate ||
      d.manufacturer ||
      d.storageCondition ||
      d.notes ||
      d.rowId,
  );
}

function humaniseStockInError(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError) {
    if (err.code === 'CONFLICT') return translateApiError(err);
    if (err.code === 'VALIDATION_ERROR') return t('addStock.errInvalidEntries');
    return translateApiError(err);
  }
  return t('addStock.errSubmitFailed');
}

// ---------------- subcomponents ----------------

interface UnitFieldProps {
  type: ItemType;
  value: string;
  onChange: (v: string) => void;
}

const CUSTOM_SENTINEL = '__custom__';

function UnitField({ type, value, onChange }: UnitFieldProps) {
  const { t } = useTranslation();
  const options = UNIT_OPTIONS[type];
  const isPreset = options.includes(value);
  const [customMode, setCustomMode] = useState(!isPreset && value.length > 0);

  // If the type changes underneath us, drop custom mode whenever the new
  // value lands on a preset.
  useEffect(() => {
    if (options.includes(value)) setCustomMode(false);
  }, [options, value]);

  return (
    <Field label={t('common.unit')}>
      {customMode ? (
        <div className="flex gap-1.5">
          <Input
            mono
            value={value}
            placeholder={t('addStock.placeholderCustomUnit')}
            onChange={(e) => onChange(e.target.value)}
          />
          <button
            type="button"
            onClick={() => {
              setCustomMode(false);
              onChange(options[0] ?? '');
            }}
            className="flex h-10 shrink-0 items-center gap-1 rounded-md border border-line bg-paper px-2.5 text-[12.5px] text-ink-2 hover:bg-paper-2"
            aria-label={t('addStock.chooseFromList')}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      ) : (
        <select
          value={isPreset ? value : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === CUSTOM_SENTINEL) {
              setCustomMode(true);
              onChange('');
              return;
            }
            onChange(v);
          }}
          className="h-10 w-full rounded-md border border-line bg-paper px-3 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {translateUnit(t, opt)}
            </option>
          ))}
          <option value={CUSTOM_SENTINEL}>{t('addStock.custom')}</option>
        </select>
      )}
    </Field>
  );
}

function EmptyList() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name="box_in" size={22} />
      </div>
      <div className="text-[13.5px] font-semibold tracking-[-0.01em]">{t('addStock.nothingInBatch')}</div>
      <div className="max-w-[260px] text-[12.5px] text-muted">{t('addStock.nothingInBatchBody')}</div>
    </div>
  );
}

interface BatchListRowProps {
  row: BatchRow;
  onEdit: (rowId: string) => void;
  onRemove: (rowId: string) => void;
}

function BatchListRow({ row, onEdit, onRemove }: BatchListRowProps) {
  const { t } = useTranslation();
  // A row may carry leftover values from a previous type if the user edited
  // it across a type switch. Only show fields the current type renders, so
  // the chips always agree with what was actually submitted.
  const show = (f: ItemFieldKey) => isFieldVisible(row.type, f);
  return (
    <div className="flex items-start gap-3 rounded-md border border-line bg-paper p-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper-2 text-muted">
        <Icon name={TYPE_ICON[row.type]} size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[14px] font-semibold tracking-[-0.01em]">{row.brandName}</span>
          {show('genericName') && row.genericName && (
            <span className="text-[13px] text-muted">{row.genericName}</span>
          )}
          {show('strength') && row.strength && (
            <span className="font-mono text-[13px] text-ink-2 tabular-nums">· {row.strength}</span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <TypePill type={row.type} size="sm" />
          {show('batchNumber') && row.batchNumber && (
            <span className="font-mono text-[11.5px] text-muted">{row.batchNumber}</span>
          )}
          {show('expiryDate') && row.expiryDate && (
            <span className="font-mono text-[11.5px] text-muted">{t('adjust.expDate', { date: row.expiryDate })}</span>
          )}
          {show('manufacturer') && row.manufacturer && (
            <span className="text-[11.5px] text-muted">{row.manufacturer}</span>
          )}
          {show('storageCondition') && row.storageCondition && (
            <span className="text-[11.5px] text-muted">{row.storageCondition}</span>
          )}
          {!row.itemId && <span className="text-[11px] text-muted-2">{t('addStock.newSku')}</span>}
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-[17px] font-semibold tabular-nums">{row.quantity}</div>
        <div className="text-[10.5px] uppercase tracking-[0.04em] text-muted">{translateUnit(t, row.unit)}</div>
      </div>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          aria-label={`${t('common.edit')} ${row.brandName}`}
          onClick={() => onEdit(row.rowId)}
          className="flex h-7 w-7 items-center justify-center rounded-md border border-line bg-paper text-muted hover:text-ink"
        >
          <Icon name="edit" size={13} />
        </button>
        <button
          type="button"
          aria-label={`${t('common.remove')} ${row.brandName}`}
          onClick={() => onRemove(row.rowId)}
          className="flex h-7 w-7 items-center justify-center rounded-md border border-line bg-paper text-muted hover:text-destructive"
        >
          <Icon name="trash" size={13} />
        </button>
      </div>
    </div>
  );
}

interface BrandSuggestFieldProps {
  type: ItemType;
  value: string;
  onChange: (v: string) => void;
  onPick: (s: MedicineSuggestion) => void;
}

// Stable key for a suggestion regardless of source — used as the React key
// when rendering the dropdown.
function suggestionKey(s: MedicineSuggestion): string {
  return s.source === 'item' ? `i:${s.itemId}` : `r:${s.nmraRegNo}`;
}

function BrandSuggestField({ type, value, onChange, onPick }: BrandSuggestFieldProps) {
  const { t } = useTranslation();
  const debounced = useDebounced(value, 200);

  // Aggregated suggestions: existing clinic items + NMRA reference catalogue,
  // already deduped+ranked server-side. `type` narrows the items half only.
  const { data: results = [] } = useMedicineSuggest({
    query: debounced,
    type,
    limit: 8,
    enabled: debounced.trim().length >= SEARCH_MIN_CHARS,
  });

  // The text here is the form's brand-name value, not state of this field's own
  // — the user is typing the name they are about to save, and a suggestion is an
  // offer to fill the rest of the form from it. That is why picking does not
  // clear the box.
  const combobox = useCombobox<MedicineSuggestion>({
    text: value,
    onTextChange: onChange,
    options: results,
    optionKey: suggestionKey,
    onSelect: onPick,
    minChars: SEARCH_MIN_CHARS,
    ariaLabel: t('common.brandName'),
  });

  return (
    <Field label={t('common.brandName')}>
      <div ref={combobox.rootRef}>
        <div ref={combobox.anchorRef}>
          <Input
            {...combobox.inputProps}
            placeholder={t('addStock.placeholderBrand')}
            autoComplete="off"
          />
        </div>

        <ComboboxListbox
          {...combobox.listboxProps}
          renderOption={(s) => (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold tracking-[-0.01em]">
                  {s.brandName ?? ''}
                </div>
                <div className="truncate text-[12px] text-muted">
                  {s.genericName ?? ''}
                  {s.strength ? ` · ${s.strength}` : ''}
                </div>
              </div>
              {s.source === 'item' ? (
                <TypePill type={s.type} size="sm" />
              ) : (
                <span className="rounded-full border border-line px-2 py-[1px] text-[10.5px] uppercase tracking-[0.04em] text-muted">
                  {t('addStock.nmraTag')}
                </span>
              )}
            </>
          )}
        />
      </div>
    </Field>
  );
}

interface AddStockScanDialogProps {
  onClose: () => void;
  onResolved: (match: Item | null, barcode: string) => void;
}

function AddStockScanDialog({ onClose, onResolved }: AddStockScanDialogProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const { data: results = [], isFetching } = useItemsSearch({
    query: code,
    limit: 5,
    enabled: code.trim().length >= 3,
  });
  const exactMatch = results.find((r) => r.barcode && r.barcode === code.trim()) ?? null;

  return (
    <ScanDialog
      onClose={onClose}
      onCodeChange={setCode}
      onUse={(c) => onResolved(exactMatch, c)}
      renderPreview={() => {
        if (isFetching) return <span className="text-muted">{t('common.lookingUp')}</span>;
        if (exactMatch) {
          return (
            <span className="text-success">
              <strong className="font-semibold">{t('addStock.matched')}</strong> {exactMatch.brandName}
              {exactMatch.strength ? ` · ${exactMatch.strength}` : ''} —{' '}
              {t(ITEM_TYPE_LABEL_KEY[exactMatch.type])}
            </span>
          );
        }
        return <span className="text-muted">{t('addStock.barcodeNoMatch')}</span>;
      }}
    />
  );
}

interface SubmitSuccessProps {
  count: number;
  units: number;
  onStartAnother: () => void;
}

function SubmitSuccess({ count, units, onStartAnother }: SubmitSuccessProps) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-12 text-center">
      <div
        className={cn(
          'flex h-[72px] w-[72px] items-center justify-center rounded-full bg-success-bg text-success',
        )}
      >
        <Icon name="check" size={36} strokeWidth={2.2} />
      </div>
      <h2 className="mt-5 text-[22px] font-semibold tracking-[-0.02em]">{t('addStock.batchReceived')}</h2>
      <p className="mt-1 max-w-[280px] text-[13.5px] leading-[1.5] text-muted">
        {t('addStock.batchReceivedBody', {
          count,
          itemLabel: t('common.item', { count }),
          units,
          unitLabel: t('common.unit', { count: units }),
        })}
      </p>
      <div className="mt-6 flex w-full flex-col gap-2.5">
        <Button size="lg" full onClick={onStartAnother}>
          <Icon name="plus" size={18} /> {t('addStock.startAnotherBatch')}
        </Button>
        <Button asChild size="lg" full variant="ghost">
          <Link to="/">{t('common.backToHome')}</Link>
        </Button>
      </div>
    </div>
  );
}
